<!-- Help content template -->
<ng-template #helpContent let-offcanvasRef>
  <div class="offcanvas-header">
    <h3 class="mb-0">{{ "Annotation tool help" | translate }}</h3>
    <button (click)="offcanvasRef.dismiss()" class="btn-close" aria-label="Close" type="button"></button>
  </div>
  <div class="offcanvas-body">
    <div class="annotation-help" style="font-size: 0.9rem">
      <h5>{{ "Getting started" | translate }}</h5>
      <p>
        {{
          "The annotation tool allows you to add shapes and notes to highlight important features in the image."
            | translate
        }}
      </p>

      <h5>{{ "Creating annotations" | translate }}</h5>
      <ul>
        <li>{{ "Use the rectangle tool to create rectangular annotations" | translate }}</li>
        <li>{{ "Use the circle tool to create circular annotations" | translate }}</li>
        <li>{{ "Use the text tool to create text annotations" | translate }}</li>
        <li>{{ "Use the link tool to create clickable link annotations" | translate }}</li>
        <li>{{ "Click and drag to draw the shape where you want it" | translate }}</li>
      </ul>

      <h5>{{ "Editing annotations" | translate }}</h5>
      <ul>
        <li>{{ "Click and drag an annotation to move it" | translate }}</li>
        <li>{{ "Use the control points (white circles) to resize a shape" | translate }}</li>
        <li>{{ "Click the text icon to add or edit a title for your annotation" | translate }}</li>
        <li>{{ "Click the trash icon to delete an annotation" | translate }}</li>
      </ul>

      <h5>{{ "Sharing annotations" | translate }}</h5>
      <ul>
        <li>{{ "Click the share button to generate a URL that includes your annotations" | translate }}</li>
        <li>{{ "The URL will be copied to your clipboard automatically" | translate }}</li>
        <li>{{ "Anyone with the link can see your annotations without changing the original image" | translate }}</li>
      </ul>

      <h5>{{ "Saving annotations (image owners only)" | translate }}</h5>
      <ul>
        <li>{{ "If you own this image, you can save annotations permanently" | translate }}</li>
        <li>{{ "Click the save button to store your annotations with the image" | translate }}</li>
        <li>{{ "Saved annotations will be visible to anyone viewing your image" | translate }}</li>
      </ul>
    </div>
  </div>
</ng-template>

<div
  [class.active]="active"
  [class.hidden-during-resize]="isResizing"
  [class.pointer-events-all]="active"
  [class.read-only-mode]="readOnly"
  class="annotation-tool-container"
  data-ignore-swipe-down="true"
>
  <!-- Fixed toolbar - only visible in edit mode -->
  <div *ngIf="!readOnly" class="fixed-top-toolbar">
    <div class="toolbar-buttons annotation-toolbar">
      <!-- Help button - always visible -->
      <button
        [disabled]="savingAnnotations"
        [ngbTooltip]="'Help' | translate"
        (click)="showHelp()"
        (touchend)="showHelp()"
        class="icon-button help-button"
        container="body"
        data-tutorial-id="annotation-help"
        placement="bottom"
        type="button"
      >
        <fa-icon [icon]="['fas', 'question']"></fa-icon>
      </button>

      <!-- Separator after help button -->
      <div class="toolbar-separator"></div>

      <!-- Drawing tools - only visible in edit mode -->
      <ng-container *ngIf="!readOnly">
        <button
          [disabled]="savingAnnotations"
          [ngbTooltip]="'Add rectangle' | translate"
          (click)="makeRect()"
          (touchend)="makeRect()"
          class="icon-button"
          container="body"
          data-tutorial-id="annotation-rectangle"
          placement="bottom"
          type="button"
        >
          <fa-icon [icon]="['fas', 'square']"></fa-icon>
        </button>
        <button
          [disabled]="savingAnnotations"
          [ngbTooltip]="'Add circle' | translate"
          (click)="makeCircle()"
          (touchend)="makeCircle()"
          class="icon-button"
          container="body"
          data-tutorial-id="annotation-circle"
          placement="bottom"
          type="button"
        >
          <fa-icon [icon]="['fas', 'circle']"></fa-icon>
        </button>
        <button
          [disabled]="savingAnnotations"
          [ngbTooltip]="'Add text' | translate"
          (click)="makeText()"
          (touchend)="makeText()"
          class="icon-button"
          container="body"
          data-tutorial-id="annotation-text"
          placement="bottom"
          type="button"
        >
          <fa-icon [icon]="['fas', 'font']"></fa-icon>
        </button>
        <button
          [disabled]="savingAnnotations"
          [ngbTooltip]="'Add link' | translate"
          (click)="makeLink()"
          (touchend)="makeLink()"
          class="icon-button"
          container="body"
          data-tutorial-id="annotation-link"
          placement="bottom"
          type="button"
        >
          <fa-icon [icon]="['fas', 'link']"></fa-icon>
        </button>
      </ng-container>

      <!-- Separator between drawing tools and action buttons -->
      <div class="toolbar-separator"></div>

      <!-- Actions - conditionally visible -->
      <ng-container *ngIf="!readOnly">
        <button
          *ngIf="isImageOwner && imageId"
          [class.saving]="savingAnnotations"
          [class.success]="saveSuccess"
          [disabled]="savingAnnotations"
          [ngbTooltip]="'Save annotations' | translate"
          (click)="saveAnnotations()"
          (touchend)="!savingAnnotations ? saveAnnotations() : null"
          (touchstart)="$event.stopPropagation()"
          class="icon-button save-button"
          container="body"
          data-tutorial-id="annotation-save"
          placement="bottom"
          type="button"
        >
          <fa-icon *ngIf="!savingAnnotations && !saveSuccess" [icon]="['fas', 'save']"></fa-icon>
          <fa-icon *ngIf="savingAnnotations" [icon]="['fas', 'spinner']" [spin]="true"></fa-icon>
          <fa-icon
            *ngIf="!savingAnnotations && saveSuccess"
            [icon]="['fas', 'check']"
            class="save-success-icon"
          ></fa-icon>
        </button>
      </ng-container>

      <!-- Share button always visible -->
      <button
        [disabled]="savingAnnotations"
        [ngbTooltip]="'Share annotations' | translate"
        (click)="shareAnnotations()"
        (touchend)="shareAnnotations()"
        (touchstart)="$event.stopPropagation()"
        class="icon-button"
        container="body"
        data-tutorial-id="annotation-share"
        placement="bottom"
        type="button"
      >
        <fa-icon [icon]="['fas', 'share-alt']"></fa-icon>
      </button>

      <!-- Exit/Clear section with a visual separator -->
      <div class="toolbar-separator"></div>

      <!-- Clear button only in edit mode -->
      <ng-container *ngIf="!readOnly">
        <button
          [disabled]="annotations.length === 0 || savingAnnotations"
          [ngbTooltip]="'Clear all' | translate"
          (click)="annotations.length > 0 ? confirmClearAll() : null"
          (touchend)="annotations.length > 0 ? confirmClearAll() : null"
          (touchstart)="$event.stopPropagation()"
          class="icon-button"
          container="body"
          data-tutorial-id="annotation-clear"
          placement="bottom"
          type="button"
        >
          <fa-icon [icon]="['fas', 'eraser']"></fa-icon>
        </button>
      </ng-container>

      <!-- Exit button always visible -->
      <button
        [disabled]="savingAnnotations"
        [ngbTooltip]="'Exit annotation mode' | translate"
        (click)="exitAnnotationModeHandler()"
        (touchend)="exitAnnotationModeHandler()"
        (touchstart)="$event.stopPropagation()"
        class="icon-button exit-button"
        container="body"
        data-tutorial-id="annotation-exit"
        placement="bottom"
        type="button"
      >
        <fa-icon [icon]="['fas', 'times']"></fa-icon>
      </button>
    </div>
  </div>

  <!-- Annotation container with HTML elements instead of SVG -->
  <!-- Hide annotations during resizing to improve performance and visual experience -->
  <div
    [class.hidden-during-resize]="isResizing"
    class="annotation-container"
    data-ignore-swipe-down="true"
    style="z-index: 999"
  >
    <!-- Annotations layer -->
    <div class="annotation-layer" data-ignore-swipe-down="true">
      <div
        *ngFor="let ann of annotations"
        [attr.data-title]="isText(ann) ? ann.title : ''"
        [class.active]="currentlyDragging === ann"
        [ngClass]="{
          annotation: true,
          rectangle: isRectangle(ann),
          circle: isCircle(ann),
          text: isText(ann),
          link: isLink(ann),
          'read-only': readOnly
        }"
        [style.--bg-color]="isText(ann) || isLink(ann) ? getColor(ann) : ''"
        [style.--height-factor]="
          isText(ann) || isLink(ann) ? Math.max(0.8, Math.min(8, getAnnotationHeight(ann) / 10)) * imageScaleFactor : 1
        "
        [style.--text-color]="isText(ann) || isLink(ann) ? '#FFFFFF' : ''"
        [style.--text-opacity]="getOpacity(ann)"
        [style.height.%]="!isText(ann) && !isLink(ann) ? getAnnotationHeight(ann) : null"
        [style.left.%]="getAnnotationLeft(ann)"
        [style.padding-bottom.%]="getShapeHandler(ann).getPaddingBottom(ann)"
        [style.top.%]="getAnnotationTop(ann)"
        [style.width.%]="!isText(ann) && !isLink(ann) ? getAnnotationWidth(ann) : null"
        (pointerdown)="
          !readOnly
            ? startDrag($event, ann, 'whole')
            : readOnly && isRegularView && (isLink(ann) || ann.linkUrl)
              ? onLinkAnnotationClick($event, ann)
              : readOnly && isRegularView
                ? onReadOnlyAnnotationClick($event)
                : null
        "
        data-ignore-swipe-down="true"
      >
        <!-- Shape element with opacity -->
        <div
          *ngIf="!isText(ann) && !isLink(ann)"
          [style.background-color]="getColor(ann)"
          [style.border-color]="getColor(ann)"
          [style.opacity]="getOpacity(ann)"
          class="shape-element"
        ></div>
        <!-- Title display (not for text annotations since they show title inside the box) -->
        <div
          *ngIf="ann.title && !isText(ann) && !isLink(ann)"
          (click)="readOnly && isRegularView ? onReadOnlyAnnotationClick($event) : null"
          class="annotation-title"
        >
          {{ ann.title }}
          <!--
               ANNOTATION ICONS LOGIC:
               - Saved annotation icon shows only for annotations that exactly match what was saved in the revision
               - Shared annotation icon shows only for annotations loaded from URL that aren't from saved annotations
               - When an annotation is modified, it gets a new ID and no longer matches either condition above
             -->
          <!-- Save icon for annotations saved by the photographer -->
          <fa-icon
            *ngIf="isFromSavedAnnotations(ann.id)"
            [icon]="['fas', 'save']"
            [ngbTooltip]="'This annotation was set by the photographer' | translate"
            class="saved-annotation-icon"
            container="body"
            placement="top"
          >
          </fa-icon>
          <!-- Share icon for annotations from URL only -->
          <fa-icon
            *ngIf="isFromUrlOnly(ann.id)"
            [icon]="['fas', 'share-alt']"
            [ngbTooltip]="'This annotation was shared as a link' | translate"
            class="url-annotation-icon"
            container="body"
            placement="top"
          >
          </fa-icon>
        </div>

        <!-- Link display for shapes with linkUrl (rectangles/circles) -->
        <div
          *ngIf="ann.linkUrl && !isLink(ann) && !isText(ann)"
          [class.has-message]="ann.message"
          (click)="onLinkAnnotationClick($event, ann)"
          (pointerdown)="$event.stopPropagation()"
          class="shape-link-info"
        >
          <fa-icon [icon]="['fas', 'link']" class="link-icon"></fa-icon>
          <span class="link-domain">{{ ann.linkUrl | domainFromUrl }}</span>
        </div>

        <!-- Link annotation display -->
        <div *ngIf="isLink(ann)" [style.cursor]="readOnly ? 'pointer' : 'move'" class="link-annotation-content">
          <fa-icon [icon]="['fas', 'link']" class="link-icon"></fa-icon>
          <div class="link-text">
            <span *ngIf="ann.title" class="link-title">{{ ann.title }}</span>
            <span *ngIf="ann.linkUrl" class="link-domain">{{ ann.linkUrl | domainFromUrl }}</span>
          </div>
        </div>

        <!-- Control points (corners) - only in edit mode -->
        <div *ngIf="isRectangle(ann) && !readOnly" class="control-points">
          <div
            (pointerdown)="startDrag($event, ann, 'tl')"
            class="control-point top-left"
            data-ignore-swipe-down="true"
          ></div>
          <div
            (pointerdown)="startDrag($event, ann, 'tr')"
            class="control-point top-right"
            data-ignore-swipe-down="true"
          ></div>
          <div
            (pointerdown)="startDrag($event, ann, 'bl')"
            class="control-point bottom-left"
            data-ignore-swipe-down="true"
          ></div>
          <div
            (pointerdown)="startDrag($event, ann, 'br')"
            class="control-point bottom-right"
            data-ignore-swipe-down="true"
          ></div>
        </div>

        <!-- Control points for circle - only top handle - only in edit mode -->
        <div *ngIf="isCircle(ann) && !readOnly" class="control-points">
          <div
            (pointerdown)="startDrag($event, ann, 'resize')"
            class="control-point top"
            data-ignore-swipe-down="true"
          ></div>
        </div>

        <!-- For text, add a resize handle at the top -->
        <div *ngIf="isText(ann) && !readOnly" class="control-points text-control-points">
          <div
            (pointerdown)="startDrag($event, ann, 'resize')"
            class="control-point top"
            data-ignore-swipe-down="true"
          ></div>
        </div>

        <!-- For link, add a resize handle at the top -->
        <div *ngIf="isLink(ann) && !readOnly" class="control-points link-control-points">
          <div
            (pointerdown)="startDrag($event, ann, 'resize')"
            class="control-point top"
            data-ignore-swipe-down="true"
          ></div>
        </div>

        <!-- Show info icon when there's a description (message) -->
        <div
          *ngIf="readOnly && ann.message"
          [class.text-annotation-button]="isText(ann)"
          (click)="$event.stopPropagation(); $event.preventDefault()"
          (mousedown)="$event.stopPropagation()"
          (pointerdown)="$event.stopPropagation(); $event.preventDefault()"
          (touchstart)="$event.stopPropagation()"
          class="control-toolbar readonly-toolbar read-only-icon"
        >
          <button
            [ngbTooltip]="'View annotation details' | translate"
            (pointerup)="showAnnotationInfo(ann); $event.stopPropagation(); $event.preventDefault()"
            class="tool-button message"
            container="body"
            placement="bottom"
          >
            <fa-icon [icon]="['fas', 'file-alt']"></fa-icon>
          </button>
        </div>

        <!-- Toolbar for each annotation - only in edit mode -->
        <div
          *ngIf="!readOnly"
          (mousedown)="$event.stopPropagation(); $event.preventDefault()"
          (pointerdown)="$event.stopPropagation(); $event.preventDefault()"
          (touchstart)="$event.stopPropagation(); $event.preventDefault()"
          class="control-toolbar"
        >
          <button
            (pointerdown)="$event.stopPropagation()"
            (pointerup)="
              showMessageForm(ann.id, annotationFormModal); $event.stopPropagation(); $event.preventDefault()
            "
            class="tool-button message"
          >
            <fa-icon [icon]="['fas', 'file-alt']"></fa-icon>
          </button>

          <button
            (pointerdown)="$event.stopPropagation()"
            (pointerup)="confirmDeleteAnnotation(ann.id); $event.stopPropagation(); $event.preventDefault()"
            class="tool-button delete"
          >
            <fa-icon icon="trash"></fa-icon>
          </button>
        </div>
      </div>
    </div>

    <!-- Drawing overlay (for creating new shapes) - hidden during loading -->
    <div *ngIf="active && !currentlyDragging" class="drawing-overlay">
      <!-- Preview of shape being drawn -->
      <div
        *ngIf="isDrawing && activeAnnotation"
        [ngClass]="{
          rectangle:
            activeAnnotation && activeAnnotation.shape && activeAnnotation.shape.type === AnnotationShapeType.RECTANGLE,
          circle:
            activeAnnotation && activeAnnotation.shape && activeAnnotation.shape.type === AnnotationShapeType.CIRCLE,
          text: activeAnnotation && activeAnnotation.shape && activeAnnotation.shape.type === AnnotationShapeType.TEXT,
          link: activeAnnotation && activeAnnotation.shape && activeAnnotation.shape.type === AnnotationShapeType.LINK
        }"
        [style.height.%]="getDrawingHeight()"
        [style.left.%]="getDrawingLeft()"
        [style.padding-bottom.%]="
          activeAnnotation
            ? getShapeHandler(_pointsToShapeProperties(activeAnnotation)).getPaddingBottom(
                _pointsToShapeProperties(activeAnnotation)
              )
            : null
        "
        [style.top.%]="getDrawingTop()"
        [style.width.%]="getDrawingWidth()"
        class="drawing-preview"
      >
        <!-- Shape element with opacity for drawing preview -->
        <div
          *ngIf="
            activeAnnotation &&
            activeAnnotation.shape &&
            activeAnnotation.shape.type !== AnnotationShapeType.TEXT &&
            activeAnnotation.shape.type !== AnnotationShapeType.LINK
          "
          [style.background-color]="getColor(activeAnnotation)"
          [style.border-color]="getColor(activeAnnotation)"
          [style.opacity]="getOpacity(activeAnnotation)"
          class="shape-element"
        ></div>
      </div>
    </div>
  </div>

  <!-- Mouse event overlay -->
  <div
    *ngIf="currentlyDragging"
    [style.cursor]="getCursorForDragMode()"
    (pointercancel)="endDrag($event)"
    (pointerleave)="endDrag($event)"
    (pointermove)="dragMove($event)"
    (pointerup)="endDrag($event)"
    class="mouse-event-overlay"
    data-ignore-swipe-down="true"
  ></div>

  <!-- NgbModal Template -->
  <ng-template #annotationFormModal let-modal>
    <div class="modal-header">
      <h4 *ngIf="pendingShapeData" class="modal-title">{{ "Create annotation" | translate }}</h4>
      <h4 *ngIf="!pendingShapeData && messageModel?.title && messageForm.disabled" class="modal-title">
        {{ messageModel.title }}
      </h4>
      <h4 *ngIf="!pendingShapeData && !messageForm.disabled" class="modal-title">
        {{ "Edit annotation" | translate }}
      </h4>
      <button [attr.aria-label]="'Close' | translate" (click)="modal.dismiss()" class="close" type="button">
        <span aria-hidden="true">&times;</span>
      </button>
    </div>
    <div class="modal-body">
      <form [formGroup]="messageForm">
        <formly-form [fields]="messageFields" [form]="messageForm" [model]="messageModel"></formly-form>
      </form>
    </div>
    <div class="modal-footer">
      <!-- Show different buttons based on edit vs view mode -->
      <ng-container *ngIf="!messageForm.disabled">
        <button (click)="modal.dismiss()" class="btn btn-secondary btn-no-block" type="button">
          {{ "Cancel" | translate }}
        </button>
        <button
          [disabled]="!messageForm.valid"
          (click)="validateAndCloseModal(modal, messageForm, messageForm.value, $event)"
          class="btn btn-primary btn-no-block"
          type="button"
        >
          {{ "Confirm" | translate }}
        </button>
      </ng-container>

      <!-- In read-only view mode, just show a close button -->
      <ng-container *ngIf="messageForm.disabled">
        <button (click)="modal.dismiss()" class="btn btn-primary btn-no-block" type="button">
          {{ "Close" | translate }}
        </button>
      </ng-container>
    </div>
  </ng-template>
</div>
